import * as React from "react";
import { styled } from "@mui/material/styles";
import {
  Button,
  Box,
  useMediaQuery,
  TextField,
  InputLabel,
} from "@mui/material";
import { KeyNumberValue } from "../interfaces";
import { apiResultLimit } from "../config";
import { useRef, useState } from "react";
import NumericalInputField from "./NumericalInputField";
import ArrowUpward from "@mui/icons-material/ArrowUpward";
import Pagination from "@mui/material/Pagination";
import Stack from "@mui/material/Stack";
import PaginationItem from "@mui/material/PaginationItem";
import ArrowLeft from "@mui/icons-material/ArrowLeft";
import ArrowRight from "@mui/icons-material/ArrowRight";
import { useBreakpoint } from "../hooks/useBreakpoint";

const PREFIX = "ResultPagination";

const classes = {
  formInputSmall: `${PREFIX}-formInputSmall`,
  formInputLarge: `${PREFIX}-formInputLarge`,
};

const StyledForm = styled("form")(({ theme }) => ({
  [`& .${classes.formInputLarge}`]: {
    "margin": 0,
    "height": 40,
    "width": 40,
    "& input": {
      padding: "0 5px",
      height: 40,
    },
    "& .MuiFormLabel-root": {
      overflow: "unset",
      padding: 0,
    },
    "& .MuiFormHelperText-root": {
      whiteSpace: "nowrap",
      width: "fit-content",
      padding: "0 5px",
      background: "#FFFFFF",
      margin: 0,
    },
  },
  [`& .${classes.formInputSmall}`]: {
    "margin": 0,
    "height": 40,
    "width": "fullWidth",
    "& input": {
      padding: "0 5px",
      height: 40,
    },
    "& .MuiFormLabel-root": {
      overflow: "unset",
      padding: 0,
    },
    "& .MuiFormHelperText-root": {
      whiteSpace: "nowrap",
      width: "fit-content",
      padding: "0 5px",
      background: "#FFFFFF",
      margin: 0,
    },
  },
}));

export interface Props {
  count: number;
  handleChangePage: (
    event: React.ChangeEvent<unknown>,
    newPage: number,
  ) => void;
  handlePickPage: (event: KeyNumberValue) => void;
  handleChangeRowsPerPage: (event: React.ChangeEvent<HTMLInputElement>) => void;
  children: React.ReactElement | React.ReactElement[];
  limit: number;
  pageNumber: number;
  hideBottomPagination?: boolean;
  useSmallSize?: boolean;
}

export default function ResultPagination({
  count,
  handleChangePage,
  handlePickPage,
  handleChangeRowsPerPage,
  children,
  limit,
  pageNumber,
  hideBottomPagination,
  useSmallSize,
}: Readonly<Props>) {
  const paginationRef = useRef(null);
  const [previousState, setPreviousState] = useState([limit, pageNumber]);
  const [pageJump, setPageJump] = useState({
    pageNumber: 0,
  });
  const handleScroll = (ref: any) => {
    ref.scrollIntoView({ behavior: "smooth" });
  };
  function submitValue(e: React.FormEvent) {
    e.preventDefault();
    if (pageJump.pageNumber !== 0 && !isNaN(pageJump.pageNumber)) {
      handlePickPage(pageJump);
      setPageJump({ pageNumber: 0 });
    }
  }
  React.useEffect(() => {
    if (JSON.stringify(previousState) !== JSON.stringify([limit, pageNumber])) {
      setPreviousState([limit, pageNumber]);
      setPageJump({ pageNumber: 0 });
    }
  }, [limit, pageJump.pageNumber, pageNumber, previousState]);
  const rowsPerPage = limit ?? apiResultLimit;
  const currentPage = pageNumber ?? 1;
  const pageCount = Math.ceil(count / rowsPerPage);
  const smallMatch = useMediaQuery("(max-width:1000px)");
  const { xs } = useBreakpoint();

  const pagination = (
    <Stack spacing={2}>
      <Pagination
        sx={(theme) => ({
          "& ul": {
            width: "max-content",
            [theme.breakpoints.down("md")]: {
              float: "left",
            },
          },
          "& button": {
            "paddingLeft": ".25rem",
            "paddingRight": ".25rem",

            "&:hover, &:focus-visible": {
              backgroundColor: theme.palette.grey[200],
            },
            "&.MuiPaginationItem-previousNext": {
              "backgroundColor": "#e0e0e0e0",
              "&:hover, &:focus-visible": {
                backgroundColor: "#d5d5d5",
              },
            },
            "&.Mui-selected": {
              "backgroundColor": theme.palette.primary.main,
              "color": "#fff",
              "&:hover, &:focus-visible": {
                backgroundColor: theme.palette.primary.dark,
              },
            },
          },
        })}
        page={currentPage}
        count={pageCount}
        shape="rounded"
        siblingCount={smallMatch ? 0 : 1}
        size={xs || useSmallSize ? "small" : "medium"}
        onChange={handleChangePage}
        renderItem={(item) => (
          <PaginationItem
            slots={{ previous: ArrowLeft, next: ArrowRight }}
            {...item}
          />
        )}
      />
    </Stack>
  );

  const goToPage = (bottom: boolean) => (
    <StyledForm
      onSubmit={submitValue}
      style={{
        display: "flex",
      }}
    >
      <NumericalInputField
        currentValue={pageJump.pageNumber}
        fieldName="pageNumber"
        id={bottom ? "pageNumberBottom" : "pageNumberTop"}
        errorText="Invalid page number"
        className={xs ? classes.formInputSmall : classes.formInputLarge}
        maxValue={pageCount}
        value={pageJump}
        setValue={setPageJump}
        shrink
        hideLabel
        ariaLabel="page number input"
      />

      <Button
        id={bottom ? "pageNumberBottomSubmit" : "pageNumberTopSubmit"}
        sx={{
          "lineHeight": 1.2,
          "textAlign": "left",
          "padding": "0 8px",
          "height": 40,
          "margin": "0 0 0 8px",
          "minWidth": 40,
          "width": 45,
          "fontSize": 12,
          "&.Mui-focusVisible": {
            backgroundColor: "rgba(0, 0, 0, 0.08)",
          },
        }}
        disabled={pageJump.pageNumber > pageCount}
        type="submit"
      >
        Go To Page
      </Button>
    </StyledForm>
  );

  const paginationComponent = (bottom: boolean) => (
    <Box
      sx={{
        justifyContent: "flex-end",
        margin: smallMatch ? "15px 0" : "0 15px",
      }}
    >
      {pagination}
    </Box>
  );

  const resultsPerPage = (
    <>
      <InputLabel
        htmlFor="result-limit"
        sx={{
          fontSize: 12,
          height: 40,
          width: 50,
          whiteSpace: "break-spaces",
          padding: 0,
          lineHeight: 1.2,
          textAlign: "right",
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          marginRight: 1,
        }}
      >
        Results
        <br />
        Per Page
      </InputLabel>

      <TextField
        id="result-limit"
        select
        sx={{
          "width": 50,
          "marginRight": 1,
          "& .MuiOutlinedInput-notchedOutline": { padding: 0 },
          "& .MuiNativeSelect-iconOutlined": { right: -2 },
          "& .MuiNativeSelect-select": {
            padding: "0 0 0 8px",
            paddingRight: "0 !important",
            height: 40,
          },
        }}
        defaultValue={rowsPerPage}
        onChange={(e: any) => handleChangeRowsPerPage(e)}
        variant="outlined"
        slotProps={{
          select: {
            native: true,
          },
        }}
      >
        <option value={10}>10</option>
        <option value={25}>25</option>
        <option value={50}>50</option>
        <option value={75}>75</option>
        <option value={100}>100</option>
      </TextField>
    </>
  );

  const paginationStack = (bottom: boolean) =>
    !xs ? (
      <Box
        sx={{
          display: "flex",
          padding: 1,
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {resultsPerPage}
        {paginationComponent(false)}
        {goToPage(bottom)}
      </Box>
    ) : (
      <Box padding={1}>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          {resultsPerPage}
          {paginationComponent(false)}
        </Box>
        {goToPage(bottom)}
      </Box>
    );

  const newDisplay = (
    <div ref={paginationRef}>
      {paginationStack(false)}
      {children}
      {hideBottomPagination ? undefined : (
        <Box
          sx={{
            display: "flex",
            padding: 1,
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Button
            id="topOfPage"
            sx={{
              "fontSize": 13,
              "margin": 0,
              "&.Mui-focusVisible": {
                backgroundColor: "rgba(0, 0, 0, 0.08)",
              },
            }}
            onClick={(e: React.MouseEvent) => {
              e.preventDefault();
              handleScroll(paginationRef.current);
            }}
          >
            top of page{" "}
            <ArrowUpward
              sx={{
                paddingLeft: 5,
              }}
            />
          </Button>
          {paginationComponent(true)}
        </Box>
      )}
    </div>
  );

  return newDisplay;
}
